import { useEffect, useState } from 'react'
import axios from 'axios';
import { Skeleton } from 'antd';
import { motion } from 'framer-motion';
import { Mail, Phone, MapPin, Facebook, Twitter, Linkedin, Github, Instagram } from 'lucide-react';
import { Link } from 'react-router-dom';

const Footer = () => {
    const [footer, setFooter] = useState({})
    const [loading, setLoading] = useState(true)

    useEffect(() => {
        fetchData()
    }, [])

    const fetchData = () => {
        axios.get('pages/65')
            .then((response) => {
                setFooter(response.data.acf)
                setLoading(false)
            })
            .catch((err) => { console.log(err) });
    }

    const navModules = [
        {
            title: "Explore",
            links: [
                { name: "Home", path: "/" },
                { name: "Products", path: "/products" },
                { name: "Case Studies", path: "/case-studies" },
            ]
        },
        {
            title: "Resources",
            links: [
                { name: "Services", path: "/services" },
                { name: "About Us", path: "/about" },
                { name: "Contact Us", path: "/contact" },
            ]
        },
    ];

    // Contact Info from Contact Page (matching the contact page data)
    const contactInfo = [
        { icon: Phone, text: "+91 8420213444", href: "tel:+918420213444", title: "Call Us" },
        { icon: Mail, text: "info@connectedbytes.in", href: "mailto:info@connectedbytes.in", title: "Email Us" },
        { icon: MapPin, text: "2A , ECOSpace Business Park , Newtown , WB-700160", href: "https://maps.google.com/?q=2A+ECOSpace+Business+Park+Newtown+WB-700160", title: "Visit Us" },
    ];

    const socialLinks = [
        { icon: Facebook, href: "https://facebook.com", color: "#1877f2", name: "Facebook" },
        { icon: Twitter, href: "https://twitter.com", color: "#1da1f2", name: "Twitter" },
        { icon: Linkedin, href: "https://linkedin.com", color: "#0077b5", name: "LinkedIn" },
        { icon: Github, href: "https://github.com", color: "#333", name: "GitHub" },
        { icon: Instagram, href: "https://instagram.com", color: "#e4405f", name: "Instagram" }
    ];

    if (loading) {
        return (
            <div className='bg-muted/30 py-12'>
                <div className='max-w-7xl mx-auto px-4'>
                    <Skeleton active title={false} paragraph={{ rows: 5 }} />
                </div>
            </div>
        );
    }

    return (
        <footer className="bg-white dark:bg-slate-950/95 backdrop-blur-xl border-t border-border transition-colors duration-300 relative overflow-hidden">
            {/* Subtle Background Glows */}
            <div className="absolute top-0 right-0 w-[400px] h-[400px] bg-primary/5 blur-[120px] rounded-full pointer-events-none" />
            <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-accent/5 blur-[120px] rounded-full pointer-events-none" />

            <div className="relative z-10">
                <div className="max-w-7xl mx-auto px-4 py-6 lg:py-8">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10">
                        
                        {/* Left Section - Logo & Company Info */}
                        <motion.div 
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5 }}
                            viewport={{ once: true }}
                            className="lg:col-span-4 space-y-4"
                        >
                            <div className="flex items-center gap-3 group">
                                <div className="relative">
                                    <div className="absolute -inset-2 bg-primary/20 rounded-full blur opacity-0 group-hover:opacity-100 transition duration-500" />
                                    <img 
                                        src="https://utfs.io/f/wzoc0SJJ8iMqmv8tcTUCV0hUWxjMFoDS38r1AKmZ5kftGwsN" 
                                        alt="Connected Bytes Software Service Logo" 
                                        className="relative h-9 w-auto z-10"
                                    />
                                </div>
                                <div>
                                    <h3 className="text-xl font-black font-heading tracking-tight leading-tight">Connected <span className="text-[#22c25d]">Bytes</span></h3>
                                    <p className="text-xs font-bold text-primary tracking-wider uppercase mt-0.5">Software Service</p>
                                </div>
                            </div>
                            
                            <p className="text-muted-foreground text-sm leading-relaxed font-body max-w-md">
                                We transform complex enterprise hurdles into elegant, high-performance software architectures. 
                                Experience the future of engineering excellence with our elite solutions.
                            </p>
                        </motion.div>

                        {/* Middle Section - Navigation Modules */}
                        <motion.div 
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, delay: 0.1 }}
                            viewport={{ once: true }}
                            className="lg:col-span-5"
                        >
                            <div className="grid grid-cols-2 sm:grid-cols-2 gap-8">
                                {navModules.map((module, idx) => (
                                    <div key={idx} className="space-y-4">
                                        <h4 className="text-sm font-black uppercase tracking-wider text-primary/80">
                                            {module.title}
                                        </h4>
                                        <ul className="space-y-3">
                                            {module.links.map((link, linkIdx) => (
                                                <li key={linkIdx}>
                                                    <Link 
                                                        to={link.path}
                                                        className="text-muted-foreground hover:text-primary text-sm transition-colors duration-300 flex items-center gap-1 group/link"
                                                    >
                                                        <span className="group-hover/link:translate-x-1 transition-transform duration-300">→</span>
                                                        {link.name}
                                                    </Link>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                ))}
                            </div>
                        </motion.div>

                        {/* Right Section - Contact Details */}
                        <motion.div 
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, delay: 0.2 }}
                            viewport={{ once: true }}
                            className="lg:col-span-3 space-y-4"
                        >
                            <div>
                                <h4 className="text-sm font-black uppercase tracking-wider text-primary/80 mb-4">
                                    Get In Touch
                                </h4>
                                <div className="space-y-0">
                                    {contactInfo.map((info, idx) => (
                                        <a 
                                            key={idx}
                                            href={info.href}
                                            className="flex items-center gap-3 group cursor-pointer"
                                            target={info.icon === MapPin ? "_blank" : "_self"}
                                            rel={info.icon === MapPin ? "noopener noreferrer" : ""}
                                            title={info.title}
                                        >
                                            <div className="w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center group-hover:bg-primary transition-all duration-300 group-hover:scale-110">
                                                <info.icon className="w-4 h-4 text-primary group-hover:text-white transition-colors" />
                                            </div>
                                            <span className="text-muted-foreground group-hover:text-primary text-sm transition-colors">
                                                {info.text}
                                            </span>
                                        </a>
                                    ))}
                                </div>
                            </div>
                        </motion.div>
                    </div>
                </div>

                {/* Bottom Bar - Compact */}
                <div className="border-t border-border/60 py-5 bg-muted/10">
                    <div className="max-w-7xl mx-auto px-4">
                        <div className="flex flex-col md:flex-row justify-between items-center gap-3">
                            <p className="text-xs text-muted-foreground text-center md:text-left">
                                © {new Date().getFullYear()} Connected Bytes Software Service. All rights reserved.
                            </p>
                            
                            {/* Social Links */}
                            <div className="flex items-center gap-4">
                                <span className="text-xs text-muted-foreground uppercase tracking-wider">Follow Us</span>
                                <div className="flex gap-2">
                                    {socialLinks.map((social, index) => {
                                        const Icon = social.icon;
                                        return (
                                            <a
                                                key={index}
                                                href={social.href}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="w-8 h-8 rounded-lg bg-muted/50 border border-border flex items-center justify-center hover:scale-110 hover:-translate-y-0.5 transition-all duration-300 group hover:bg-gradient-to-br hover:from-primary hover:to-accent hover:border-transparent"
                                                aria-label={social.name}
                                            >
                                                <Icon className="w-4 h-4 text-muted-foreground group-hover:text-white transition-colors" />
                                            </a>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </footer>
    )
}

export default Footer